<template>
  <ElDialog
    ref="modal"
    v-model="innerVisibility"
    :close-on-click-modal="false"
    class="verify-account-modal el-dialog--no-header el-dialog--compact-footer"
  >
    <div class="verify-account-modal__left">
      <div class="verify-account-modal__header">
        <h3 class="verify-account-modal__headline">
          {{ $t("profile.account.verification.headline") }}
        </h3>
        <h2 class="verify-account-modal__title">
          {{ $t("profile.account.verification.title") }}
        </h2>
        <i18n-t
          class="verify-account-modal__subtitle"
          keypath="profile.account.verification.subtitle"
          tag="p"
        >
          <template #email>
            <span class="verify-account-modal__email-highlight">
              {{ authUserEmail }}
            </span>
          </template>
        </i18n-t>
      </div>
      <div class="verify-account-modal__body">
        <div
          v-for="(client, index) in emailClients"
          :key="index"
          class="verify-account-modal__email-item"
          @click="onClickEmailClient(client.host)"
        >
          <div class="verify-account-modal__email-item-content">
            <PxIcon
              :name="client.icon"
              class="verify-account-modal__email-item-icon"
              size="28"
            />
            <span class="verify-account-modal__email-item-text">
              {{ $t("common.open") }} {{ client.name }}
            </span>
          </div>
          <PxIcon
            name="arrow"
            class="verify-account-modal__email-item-chevron"
            size="16"
          />
        </div>
      </div>
      <div class="verify-account-modal__footer">
        <i18n-t
          class="verify-account-modal__tip"
          keypath="profile.account.verification.help"
          tag="span"
        >
          <template #link>
            <PxButton
              :disabled="hasJustResentConfirmation"
              class="verify-account-modal__resend-btn"
              type="link"
              @click="onClickResendConfirmation"
            >
              {{ accountModalTip }}
            </PxButton>
          </template>
        </i18n-t>
      </div>
    </div>
    <div class="verify-account-modal__right">
      <div class="verify-account-modal__image">
        <img :alt="modalImage.alt" :src="modalImage.url" />
      </div>
    </div>
  </ElDialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import ElDialogMixin from "@/mixins/el-dialog.mixin";
import { resendEmailProvider } from "@/modules/authentication/services/data/resend-email/resend-email.provider";

export default defineComponent({
  name: "VerifyAccountModal",

  mixins: [ElDialogMixin],

  data() {
    return {
      // Time in seconds that a user needs to wait before
      // asking for another email confirmation.
      resendWaitPeriod: 20000,
      hasJustResentConfirmation: false,

      emailClients: [
        {
          name: "Gmail",
          icon: "gmail",
          host: "https://mail.google.com",
        },
        {
          name: "Outlook",
          icon: "outlook",
          host: "https://outlook.live.com",
        },
        {
          name: "Yahoo",
          icon: "yahoo",
          host: "https://mail.yahoo.com",
        },
      ],
    };
  },

  computed: {
    modalImage() {
      return this.$tm("authentication.login.modal.image") as {
        url: string;
        alt: string;
      };
    },

    authUserEmail(): string {
      return this.$store.get("auth/user.email");
    },

    accountModalTip() {
      return this.hasJustResentConfirmation
        ? this.$t("profile.account.verification.linkWait")
        : this.$t("profile.account.verification.link");
    },
  },

  methods: {
    onClickEmailClient(emailClientDomain: string) {
      window.open(emailClientDomain);
    },

    async onClickResendConfirmation() {
      await resendEmailProvider.create({
        email: this.authUserEmail,
      });

      // TODO: Use PxModalMessage instead of Element Message
      // Shows message under Navbar, due to message global context,
      // which will be shown behind modal overlay background
      this.$message({
        message: this.$t(
          "authentication.login.messages.confirmationEmail",
        ) as string,
        type: "success",
        customClass: "is-navbar",
      });

      // Renable email resend after the wait period has finished.
      this.hasJustResentConfirmation = true;
      setTimeout(() => {
        this.hasJustResentConfirmation = false;
      }, this.resendWaitPeriod);
    },
  },
});
</script>

<style lang="scss">
.verify-account-modal {
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-top: 0 !important;

  @include breakpoint-down(sm) {
    margin-top: 0 !important;
  }

  @include breakpoint-up(md) {
    // override default
    width: 60%;
  }

  .el-dialog__header {
    padding-bottom: 0;
    
    // Ensure close icon is visible and correctly positioned on mobile
    @include breakpoint-down(sm) {
      display: block;
      padding: 0;
      height: 0;
        .el-dialog__headerbtn {
          top: 4px;
          right: 4px;
          z-index: 10;
          font-size: 24px;
          background-image: url("/img/icons/cross--grey.svg");
          background-size: 24px;
          background-repeat: no-repeat;
          background-position: center;
          width: 44px;
          height: 44px;
        }
    }
  }

  .el-dialog__body {
    display: grid;
    grid-template-columns: auto;
    box-shadow: 0 2px 16px 0 rgba(39, 39, 64, 0.1);

    @include breakpoint-up(md) {
      grid-template-columns: 60% auto;
      width: 100%;
      max-height: 570px;
      padding: 0;
    }

    @include breakpoint-down(sm) {
      padding: 1rem;
      border-radius: 12px;
      box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
    }
  }
}

.verify-account-modal__left {
  padding: 1rem 0;

  @include breakpoint-up(md) {
    padding: 3rem 2.5rem;
  }

  @include breakpoint-down(sm) {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding-top: 2rem;
  }
}

.verify-account-modal__right {
  overflow: hidden;

  @include breakpoint-down(sm) {
    display: none;
  }

  background-image: linear-gradient(
    to bottom,
    rgba(255, 129, 65, 0.06),
    rgba(255, 129, 65, 0.04) 1%,
    rgba(255, 129, 65, 0.06)
  );
}

.verify-account-modal__image {
  height: 100%;
  background-image: url("#{$assetsPath}/img/illustrations/verify-account/clouds.svg");
  background-repeat: no-repeat;
  background-position: 65% center;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.verify-account-modal__header {
  @include breakpoint-down(sm) {
    margin-bottom: 30px;
  }
}

.verify-account-modal__headline {
  @include grotesk(medium);
  margin-bottom: 1px;
  font-size: 2rem;
  line-height: 40px;
  font-weight: 600;
  color: #7bb7ff; // Malibu blue globally as per image
  letter-spacing: -0.7px;

  @include breakpoint-down(sm) {
    margin-bottom: 12px;
    line-height: 1.1;
  }
}

.verify-account-modal__title {
  @include grotesk(semiBold);
  margin-bottom: 8px;
  font-size: 1.8rem;
  line-height: 35px;
  color: $ebony-clay;
  letter-spacing: -0.26px;

  @include breakpoint-down(sm) {
    font-size: 1.5rem;
    line-height: 1.25;
    margin-bottom: 18px;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
  }
}

.verify-account-modal__subtitle {
  @include grotesk(regular);
  margin-top: 9px;
  font-size: 1.2rem;
  line-height: 22px;
  color: $manatee;
  letter-spacing: -0.36px;

  @include breakpoint-down(sm) {
    color: $gun-powder;
    margin: 0;
    font-size: 1.2rem;
  }

  .verify-account-modal__email-highlight {
    @include grotesk(bold);
    color: $ebony-clay;
    font-size: 1.2rem;
    overflow-wrap: anywhere;
    display: inline-block;
  }
}

.verify-account-modal__body {
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  margin-top: 3rem;
  margin-bottom: 3rem;
  width: 100%;

  @include breakpoint-down(sm) {
    gap: 12px;
    margin-top: 0;
    margin-bottom: 30px;
  }
}

.verify-account-modal__email-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;

  // Desktop (default or md up) behavior: looks like standard buttons
  @include breakpoint-up(md) {
    // height: 37px;
    padding: 0.7rem;
    border: 1px solid $mischka;
    border-radius: 4px;
    margin-bottom: 15px;
    color: $gun-powder;

    &:hover {
      background-color: $pale-grey-two;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  // Mobile behavior: looks like card components as per image
  @include breakpoint-down(sm) {
    padding: 14px 18px;
    background: $white;
    border: 1px solid $pale-grey;
    border-radius: 10px;

  }

  &-content {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &-text {
    @include grotesk(medium);
    font-size: 16px;
    color: $gun-powder;

    @include breakpoint-down(sm) {
      color: $manatee;
    }
  }

  &-icon.px-icon {
    width: 30px;
    height: 30px;
    @include breakpoint-up(md) {
      margin-right: -4px; 
    }
  }

  &-chevron {
    color: $mischka;
    transform: rotate(-90deg);

    @include breakpoint-up(md) {
      display: none; 
    }
  }
}

.verify-account-modal__footer {
  @include breakpoint-down(sm) {
    text-align: center;
  }
}

.verify-account-modal__tip {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 1.2rem;
  white-space: nowrap;

  @include breakpoint-down(sm) {
    flex-direction: column;
    gap: 0;
    white-space: normal;
  }
}

.verify-account-modal__footer-instruction {
  @include grotesk(regular);
  font-size: 15px;
  color: $gun-powder;
  margin-bottom: 2px;

  @include breakpoint-up(md) {
    display: none; // Only visible in mobile-style layout
  }
}

.verify-account-modal__resend-btn {
  &.el-button--link {
    flex-shrink: 0;
    padding: 0;
    height: auto;
    text-decoration: none; 

    span {
      @include grotesk(medium);
      font-size: 1.2rem;
      color: #7bb7ff; 
      text-decoration: none; 
    }

    @include breakpoint-down(sm) {
      span {
        font-size: 1.2rem;
      }
    }

    &:hover span {
      color: darken(#7bb7ff, 15%);
      text-decoration: none; 
    }
  }
}
</style>
